import React, { useState, useEffect, useCallback, useRef } from 'react'
import axios from '../../../config/axios'
import NewProductModal from './ProductsModal/NewProductModal'
import ProductPreviewModal from './ProductsPreview/ProductPreviewModal'

import ProductsFilterModal, { FilterStatus } from './filterproduct';

import {
  PageContainer,
  Header,
  Title,
  ButtonGroup,
  FilterButton,
  NewProductButton,
  TableContainer,
  Actions,
  ActionButton,
} from './style'
import { GlobalTable, HeaderLine, Td, Tr, FooterTr, Th } from '../../../components/styles/table-style'
import LoadMoreButton from '../../../components/LoadMoreButton'
import { LoadMoreProvider, useLoadMore } from '../../../contexts/LoadMoreContext'


import SearchIcon from '../../../assets/produtcss/search-in.svg'
import FilterIcon from '../../../assets/produtcss/filter.svg'
import SwitchOnIcon from '../../../assets/produtcss/Switch.svg'
import SwitchOffIcon from '../../../assets/produtcss/Switch not.svg'
import plusOn from '../../../assets/produtcss/Plus.svg'

type ProductPayloadType = {
  id?: number;
  name: string;
  price: number;
  size: (string | number)[];
  status: boolean;
  collection: string;
  family: string;
  groupName: string;
  colors: string[];
  pipefyId: string
};

type ProductType = {
  id: number;
  pipefyId: string;
  code: string;
  name: string;
  collection: string;
  family: string;
  type: string;
  groupName?: string;
  colors: string;
  graduation: string;
  price?: string;
  status?: boolean;
  groupId?: number | null;
  group?: {
    id: number;
    name?: string;
    familyId?: number | null;
    family?: {
      id: number;
      name: string;
      colors?: { id: number; name: string; hexCode?: string }[];
    };
  };
};

const formatPrice = (price: number | string): string => {
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

  if (isNaN(numericPrice)) {
    return 'R$ 0,00';
  }

  return numericPrice.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

const parsePrice = (priceString: string): number => {
  if (!priceString) return 0;

  const cleaned = priceString
    .replace(/[^\d,.]/g, '')
    .replace(/\./g, '')
    .replace(',', '.')

  const numericPrice = parseFloat(cleaned);
  return isNaN(numericPrice) ? 0 : numericPrice;
};

const ProductsPageContent = () => {
  const { hasNextPage, isFetchingNextPage, setIsFetchingNextPage, setFetchNextPageFunction, fetchNextPage, setHasNextPage } = useLoadMore();

  const [allProducts, setAllProducts] = useState<ProductType[]>([])
  const [visibleProducts, setVisibleProducts] = useState<ProductType[]>([])

  const [checkedProducts, setCheckedProducts] = useState<{
    [key: number]: boolean
  }>({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [productToEdit, setProductToEdit] = useState<any | null>(null)
  const [previewProduct, setPreviewProduct] = useState<ProductType | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const [page, setPage] = useState(0)
  const [pageSize] = useState(10)
  const [totalCount, setTotalCount] = useState<number>(0)

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [currentProductFilterStatus, setCurrentProductFilterStatus] =
    useState<FilterStatus>('all')

  const filterButtonRef = useRef<HTMLButtonElement>(null)
  const [filterButtonPosition, setFilterButtonPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  })

  useEffect(() => {
    if (isFilterModalOpen && filterButtonRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect()
      setFilterButtonPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      })
    }
  }, [isFilterModalOpen]);

  const extractUniqueColorsFromBackend = (productBackend: any): string[] => {

    let colorsFromBackend: string[] = [];

    if (productBackend.colors && Array.isArray(productBackend.colors)) {
      colorsFromBackend = productBackend.colors.map((color: any) =>
        typeof color === 'string' ? color : (color.name || String(color))
      );
    }

    else if (productBackend.group?.family?.colors && Array.isArray(productBackend.group.family.colors)) {
      colorsFromBackend = productBackend.group.family.colors.map((color: any) =>
        typeof color === 'string' ? color : (color.name || String(color))
      );
    }

    const uniqueColors = Array.from(new Set(
      colorsFromBackend
        .filter(color => color && typeof color === 'string' && color.trim() !== '')
        .map(color => color.trim())
    ));

    return uniqueColors;
  };

  const formatProductData = (productBackend: any): ProductType => {
    const collectionName = productBackend.group?.family?.collection?.name || 'N/A';
    const familyName = productBackend.group?.family?.name || 'N/A';
    const groupName = productBackend.group?.name || 'N/A';

    const uniqueColors = extractUniqueColorsFromBackend(productBackend);
    const formattedColors = uniqueColors.length > 0 ? uniqueColors.join('; ') : 'N/A';

    const formattedGraduation = Array.isArray(productBackend.size)
      ? productBackend.size.join(', ')
      : 'N/A';

    const formattedPrice = formatPrice(productBackend.price || 0);

    return {
      id: productBackend.id,
      pipefyId: productBackend.pipefyId,
      code: String(productBackend.id),
      name: productBackend.name,
      collection: collectionName,
      family: familyName,
      type: groupName,
      colors: formattedColors,
      graduation: formattedGraduation,
      price: formattedPrice,
      status: productBackend.status !== undefined ? productBackend.status : true,
      groupId: productBackend.groupId,
      group: productBackend.group,
    };
  };

  const fetchProducts = useCallback(
    async (
      currentPage: number,
      currentSize: number,
      statusFilter: FilterStatus,
      append: boolean = false,
    ) => {
      try {
        setIsFetchingNextPage(true);

        let url = `/backend-fashion-orders/fashion-product/paginated?page=${currentPage}&size=${currentSize}`

        if (statusFilter === 'active') {
          url += '&status=true'
        } else if (statusFilter === 'inactive') {
          url += '&status=false'
        }

        console.log('🔍 [PRODUCTS] Fetching products with filter:', statusFilter, 'URL:', url, 'Page:', currentPage, 'Append:', append);

        const response = await axios.get(url)
        const rawData = response.data
        const productsFromBackend = rawData.content || [];

        console.log('📦 [PRODUCTS] Received products:', productsFromBackend.length, 'Total:', rawData.totalElements, 'Last:', rawData.last);

        const formattedProducts: ProductType[] = productsFromBackend.map(formatProductData);
        const totalElements = rawData.totalElements || 0;
        
        if (currentPage === 0 || !append) {
          setAllProducts(formattedProducts);
          const hasMore = formattedProducts.length < totalElements;
          setHasNextPage(hasMore);
          console.log('🆕 [PRODUCTS] First load - hasMore:', hasMore, 'formattedProducts.length:', formattedProducts.length, 'totalElements:', totalElements);
        } else {
          setAllProducts((prev) => {
            const existingIds = new Set(prev.map(p => p.id));
            const newProducts = formattedProducts.filter(p => !existingIds.has(p.id));
            const updatedProducts = [...prev, ...newProducts];
            const hasMore = updatedProducts.length < totalElements;
            setHasNextPage(hasMore);
            console.log('➕ [PRODUCTS] Append - hasMore:', hasMore, 'prev.length:', prev.length, 'newProducts.length:', newProducts.length, 'updatedProducts.length:', updatedProducts.length, 'totalElements:', totalElements);
            return updatedProducts;
          });
        }
        
        setTotalCount(totalElements);

        const initialChecked = formattedProducts.reduce((acc, product) => {
          if (product.id !== undefined) {
            acc[product.id] = product.status || false
          }
          return acc
        }, {} as { [key: number]: boolean })
        setCheckedProducts((prev) => ({ ...prev, ...initialChecked }))
      } catch (error) {
        console.error('Erro ao buscar produtos:', error)
      } finally {
        setIsFetchingNextPage(false);
      }
    },
    [setIsFetchingNextPage, setHasNextPage],
  )

  useEffect(() => {
    setAllProducts([]);
    setPage(0);
    fetchProducts(0, pageSize, currentProductFilterStatus, false);
  }, [currentProductFilterStatus, pageSize, fetchProducts]);

  useEffect(() => {
    if (page > 0) {
      fetchProducts(page, pageSize, currentProductFilterStatus, true)
    }
  }, [page, pageSize, currentProductFilterStatus, fetchProducts])

  useEffect(() => {
    setVisibleProducts(allProducts);
  }, [allProducts]);

  useEffect(() => {
    const loadMoreFunction = () => {
      if (!isFetchingNextPage) {
        setPage((prevPage) => prevPage + 1);
      }
    };
    setFetchNextPageFunction(loadMoreFunction);
  }, [setFetchNextPageFunction, isFetchingNextPage]);


  const updateProductStatus = async (
    productId: number,
    newStatus: boolean,
    pipefyId?: string,
    productData?: ProductType,
  ) => {
    try {
      const url = `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/${productId}`

      const payload = {
        status: newStatus,
        pipefyId: pipefyId,
        productData: productData,
      }

      await axios.put(url, payload)

      setPage(0)
      setAllProducts([])
      fetchProducts(0, pageSize, currentProductFilterStatus, false)
    } catch (error: any) {
      console.error('Erro ao atualizar status do produto:', error.message)
      alert(`Erro ao atualizar status do produto: ${error.message}`)
    }
  }

  const statusCheck = async (productId: number) => {
    // 1. Encontre o produto no estado local para obter todos os dados, incluindo o pipefyId
    const productToUpdate = allProducts.find((p) => p.id === productId)

    if (!productToUpdate) {
      console.error('Erro: Produto não encontrado no estado local.')
      return
    }

    const currentStatus = checkedProducts[productId]
    const newStatus = !currentStatus

    // Atualiza o estado local imediatamente para feedback visual
    setCheckedProducts((prev) => ({
      ...prev,
      [productId]: newStatus,
    }))

    // 2. Chame a função de atualização com os dados necessários para o backend
    await updateProductStatus(
      productId,
      newStatus,
      productToUpdate.pipefyId,
      productToUpdate,
    )
  }

  const handleEdit = (product: ProductType) => {
    let colorsArray: string[] = [];
    if (product.colors && product.colors !== 'N/A') {
      const rawColors = product.colors.includes(';')
        ? product.colors.split(';')
        : product.colors.split(',');

      colorsArray = Array.from(new Set(
        rawColors
          .map(c => c.trim())
          .filter(c => c !== '' && c !== 'N/A')
      ));
    }

    let gradValuesArray: (string | number)[] = [];
    if (product.graduation && product.graduation !== 'N/A') {
      gradValuesArray = product.graduation
        .split(',')
        .map((v) => {
          const trimmed = v.trim();
          return isNaN(Number(trimmed)) ? trimmed : Number(trimmed);
        })
        .filter(Boolean);
    }

    const formattedPriceForModal = product.price || ''

    setProductToEdit({
      id: product.id,
      code: product.code,
      name: product.name,
      collection: product.collection,
      family: product.family,
      type: product.type,
      colors: colorsArray,
      gradValues: gradValuesArray,
      pricing: formattedPriceForModal,
      status: product.status,
    })
    setIsModalOpen(true)
  }

  const handleSaveProduct = async (productData: {
    id?: number;
    name: string;
    collection: string;
    family: string;
    type: string;
    colors: string[];
    gradValues: (string | number)[];
    pricing: string;
    status?: boolean;
    pipefyId: string;
    createInPipefy?: boolean;
  }) => {
    try {
      const rawPrice = parsePrice(productData.pricing);

      const cleanedColors = productData.colors
        .filter(color => color && typeof color === 'string')
        .map(color => color.trim())
        .filter(color => color !== '' && color !== 'N/A')
        .filter(color => color.length > 0);

      const uniqueColors = Array.from(new Set(cleanedColors));

      const finalColors = uniqueColors.length > 0 ? uniqueColors : [];

      if (productData.id) {
        const existingProduct = allProducts.find(p => p.id === productData.id);

        if (!existingProduct) {
          console.error('Product not found in state. Cannot update.');
          alert('Product not found in state. Cannot update.');
          return;
        }

        const payloadOrdeno: any = {
          id: productData.id,
          name: productData.name,
          family: productData.family,
          type: existingProduct.type || productData.type,
          size: productData.gradValues,
          collection: productData.collection,
          groupName: existingProduct.groupName || productData.type,
          colors: finalColors,
          price: rawPrice,
          status: productData.status,
          pipefyId: existingProduct.pipefyId
        };

        /* if (productData.id) {
          payload.id = productData.id;
        } */

        const urlOrdeno = `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/${productData.id}`;
        await axios.put(urlOrdeno, payloadOrdeno, {
          headers: { 'Content-Type': 'application/json' },
        });
      } else {
        let pipefyId = '';
        
        // Só cria no Pipefy se o switch estiver habilitado
        if (productData.createInPipefy !== false) {
          // 1️⃣ Preparar payload para Pipefy
          const payloadPipefy = {
            name: productData.name,
            referencia: "",
            colors: productData.colors,
            groupName: productData.type,
            material: "",
            colecao: "",
            family: productData.family,
            size: productData.gradValues,
            price: rawPrice,
          };

          const integrationUrl = process.env.REACT_APP_INTEGRATION_URL || 'https://integracao-nao-nativa-de-campos-260311756054.southamerica-east1.run.app';
          const pipefyUrl = `${integrationUrl}/pipefy-webhook/ordeno-product-created`;
          const responsePipefy = await axios.post(pipefyUrl, payloadPipefy, {
            headers: {
              'Content-Type': 'application/json',
            },
          });

          const pipefyObj = responsePipefy.data;
          pipefyId = pipefyObj.details.id; // ID gerado no Pipefy
        }

        // 3️⃣ Preparar payload para Ordeno (API local)
        const payloadOrdeno: any = {
          name: productData.name,
          family: productData.family,
          collection: productData.collection,
          groupName: productData.type,
          type: productData.type,
          colors: productData.colors,
          size: productData.gradValues.map(s => {
            // Converter strings para números
            const num = typeof s === 'string' ? parseInt(s, 10) : s;
            return isNaN(num) ? 0 : num;
          }),
          price: rawPrice,
          pipefyId: pipefyId || undefined, // Só inclui se tiver ID do Pipefy
          status: true,
        };

        // 4️⃣ POST para Ordeno
        const url = `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product`;
        await axios.post(url, payloadOrdeno);
      }

      setPage(0);
      setAllProducts([]);
      fetchProducts(0, pageSize, currentProductFilterStatus, false);
      handleCloseModal();

    } catch (error: any) {
      const msg = error?.response?.data?.message || error.message || 'Erro desconhecido';
      console.error('Erro ao salvar produto:', msg);
      alert(`Erro ao salvar produto: ${msg}`);
    }
  };

  const handleDelete = (productId: number) => {
    setAllProducts((prev) => prev.filter((product) => product.id !== productId))
  }

  const handlePreviewProduct = (product: ProductType) => {
    setPreviewProduct(product)
    setIsPreviewOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setProductToEdit(null)
  }

  const handleOpenEditModalFromPreview = (product: ProductType | any) => {
    setIsPreviewOpen(false)
    setPreviewProduct(null)
    handleEdit(product)
  }

  const handleApplyFilter = (status: FilterStatus) => {
    setCurrentProductFilterStatus(status)
    setIsFilterModalOpen(false)
  }

  const handleOpenFilterModal = () => {
    setIsFilterModalOpen(true)
  }

  return (
    <PageContainer>
      {' '}
      <Header>
        <Title>Produtos</Title>
        <ButtonGroup>
          <FilterButton ref={filterButtonRef} onClick={handleOpenFilterModal}>
            <img src={FilterIcon} alt='Filtrar' width={14} height={14} />
            Filtrar
          </FilterButton>

          <NewProductButton
            onClick={() => {
              setProductToEdit(null)
              setIsModalOpen(true)
            }}
          >
            <img src={plusOn} alt='Novo produto' width={18} height={18} />
            Novo produto
          </NewProductButton>
        </ButtonGroup>
      </Header>
      <TableContainer>
        <GlobalTable>
          <thead>
            <HeaderLine>
              <Th></Th>
              <Th>Código</Th>
              <Th>Produto</Th>
              <Th>Coleção</Th>
              <Th>Família</Th>
              <Th>Grupo</Th>
              <Th>Cores</Th>
              <Th>Graduação</Th>
              <Th>Preço</Th>
              <Th></Th>
            </HeaderLine>
          </thead>
          <tbody>
            {visibleProducts.length > 0 ? (
              visibleProducts.map((product) => (
                <Tr key={product.id}>
                  <Td>
                    <button
                      onClick={() => statusCheck(product.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      <img
                        src={
                          checkedProducts[product.id]
                            ? SwitchOnIcon
                            : SwitchOffIcon
                        }
                        alt='Check'
                        width={52}
                        height={32}
                      />
                    </button>
                  </Td>
                  <Td style={{ color: '#202020' }}>
                    {product.code}
                  </Td>
                  <Td>{product.name}</Td>
                  <Td>{product.collection}</Td>
                  <Td>{product.family}</Td>
                  <Td>{product.type}</Td>
                  <Td>{product.colors}</Td>
                  <Td>{product.graduation}</Td>
                  <Td>{product.price}</Td>
                  <Td>
                    <Actions>
                      <ActionButton
                        onClick={() => handlePreviewProduct(product)}
                      >
                        <img
                          src={SearchIcon}
                          alt='Visualizar'
                          width={24}
                          height={24}
                        />
                      </ActionButton>
                    </Actions>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={10} style={{ textAlign: 'center', padding: '20px' }}>
                  Nenhum produto encontrado com o filtro atual.
                </Td>
              </Tr>
            )}
          </tbody>
          <tfoot>
            <FooterTr>
              <Td colSpan={10} style={{ textAlign: 'center', background: 'transparent', padding: '1rem 2rem' }}>
                <LoadMoreButton
                  currentCount={visibleProducts.length}
                  totalCount={totalCount}
                  onLoadMore={fetchNextPage}
                  isLoading={isFetchingNextPage}
                  hasMore={hasNextPage}
                />
              </Td>
            </FooterTr>
          </tfoot>
        </GlobalTable>
      </TableContainer>
      <NewProductModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        productToEdit={productToEdit}
        onSave={handleSaveProduct}
      />
      <ProductPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        product={previewProduct}
        onEdit={handleOpenEditModalFromPreview}
      />
      <ProductsFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilter={handleApplyFilter}
        currentFilterStatus={currentProductFilterStatus}
        top={filterButtonPosition.top}
        left={filterButtonPosition.left}
        width={filterButtonPosition.width}
      />
    </PageContainer>
  )
}

const ProductsPage = () => (
  <LoadMoreProvider>
    <ProductsPageContent />
  </LoadMoreProvider>
)

export default ProductsPage
